/**
 * Tile geometry for the full-screen video grid (issue #123, epic #122 §3.2).
 *
 * Pure functions: no DOM, no React. The layout component measures the video
 * area and hands the numbers in; everything that decides where a tile goes
 * lives here, so it can be tested without a browser.
 */

/**
 * How many tiles are sized to fit the area. Beyond this the tiles keep the
 * twelve-tile size and the extra rows scroll vertically (epic D8).
 */
export const MAX_FITTED_TILES = 12;

/** Widest tile allowed: 16:9. */
export const MAX_TILE_ASPECT = 16 / 9;

/** Narrowest tile allowed on a landscape area: 4:3. */
export const MIN_TILE_ASPECT = 4 / 3;

/**
 * Narrowest tile allowed on a portrait area (a phone): 3:4, so a tall screen
 * is not mostly empty.
 */
export const MIN_PORTRAIT_TILE_ASPECT = 3 / 4;

export interface FitTilesOptions {
  /**
   * Relax the lower aspect bound to 3:4. Defaults to "the area is taller than
   * it is wide".
   */
  portrait?: boolean;
}

/** The grid {@link fitTiles} chose, and the size of every tile in it. */
export interface TileFit {
  /** Columns of the grid */
  columns: number;
  /** Rows needed for *all* the tiles, including any that scroll */
  rows: number;
  /** Tile width in whole pixels */
  width: number;
  /** Tile height in whole pixels */
  height: number;
}

const EMPTY_FIT: TileFit = { columns: 0, rows: 0, width: 0, height: 0 };

/**
 * The largest tile for `count` participants in a `width` × `height` area.
 *
 * For `min(count, 12)` tiles, every `columns × rows` grid that holds them is
 * tried. In each cell the tile is the largest box whose aspect ratio stays
 * between 4:3 (3:4 when portrait) and 16:9, so a tile may be narrower or
 * shorter than its cell: margins are allowed, cropping a two-person call into
 * tall portraits is not. The grid with the largest tile wins; on a tie the
 * one with fewer columns does.
 *
 * With more than twelve tiles the size stays the twelve-tile size and `rows`
 * grows, so the caller scrolls the extra rows instead of shrinking everyone.
 *
 * On a 1392 × 732 area (1440 × 900 minus the top bar and padding), with a
 * 12px gap: 1 → 1301 × 732, 2 → 690 × 517, 3 → 640 × 360, 12 → 339 × 236.
 */
export function fitTiles(
  count: number,
  width: number,
  height: number,
  gap: number,
  options: FitTilesOptions = {}
): TileFit {
  const total = Math.max(0, Math.floor(count));
  if (total === 0 || width <= 0 || height <= 0) return EMPTY_FIT;

  const portrait = options.portrait ?? height > width;
  const minAspect = portrait ? MIN_PORTRAIT_TILE_ASPECT : MIN_TILE_ASPECT;
  const fitted = Math.min(total, MAX_FITTED_TILES);

  let best: { columns: number; width: number; height: number } | null = null;

  for (let columns = 1; columns <= fitted; columns++) {
    const rows = Math.ceil(fitted / columns);
    const cellWidth = (width - gap * (columns - 1)) / columns;
    const cellHeight = (height - gap * (rows - 1)) / rows;
    if (cellWidth <= 0 || cellHeight <= 0) continue;

    let tileWidth = cellWidth;
    let tileHeight = cellHeight;
    const aspect = cellWidth / cellHeight;
    if (aspect > MAX_TILE_ASPECT) {
      tileWidth = cellHeight * MAX_TILE_ASPECT;
    } else if (aspect < minAspect) {
      tileHeight = cellWidth / minAspect;
    }

    if (!best || tileWidth * tileHeight > best.width * best.height) {
      best = { columns, width: tileWidth, height: tileHeight };
    }
  }

  if (!best) return EMPTY_FIT;

  return {
    columns: best.columns,
    rows: Math.ceil(total / best.columns),
    width: Math.floor(best.width),
    height: Math.floor(best.height)
  };
}

/** Where one tile sits inside the grid box, in pixels from its top-left. */
export interface TilePosition {
  left: number;
  top: number;
}

/**
 * Top-left corner of each of `count` tiles laid out by `fit`, row by row.
 *
 * Every row but the last is full. The last row is centred horizontally in the
 * grid box ({@link gridBox}), so three people read as two on top and one
 * centred below rather than a ragged left-aligned row.
 */
export function tilePositions(count: number, fit: TileFit, gap: number): TilePosition[] {
  const { columns, width, height } = fit;
  if (count <= 0 || columns <= 0) return [];

  const rows = Math.ceil(count / columns);
  const boxWidth = columns * width + (columns - 1) * gap;
  const positions: TilePosition[] = [];

  for (let index = 0; index < count; index++) {
    const row = Math.floor(index / columns);
    const column = index % columns;
    const inRow = row === rows - 1 ? count - row * columns : columns;
    const rowWidth = inRow * width + (inRow - 1) * gap;
    const offset = (boxWidth - rowWidth) / 2;

    positions.push({
      left: Math.round(offset + column * (width + gap)),
      top: row * (height + gap)
    });
  }

  return positions;
}

/** Size of the box that holds every tile of `fit`, scrolled rows included. */
export function gridBox(fit: TileFit, gap: number): { width: number; height: number } {
  if (fit.columns <= 0 || fit.rows <= 0) return { width: 0, height: 0 };
  return {
    width: fit.columns * fit.width + (fit.columns - 1) * gap,
    height: fit.rows * fit.height + (fit.rows - 1) * gap
  };
}

/**
 * How many tiles are still below the visible part of a scrolled grid.
 *
 * A tile counts as seen once its row is *fully* on screen, so a row peeking in
 * at the bottom edge is still "below". `visibleBottom` is the bottom of the
 * visible window in grid coordinates (0 is the top of the first row).
 */
export function tilesBelow(
  count: number,
  fit: TileFit,
  gap: number,
  visibleBottom: number
): number {
  if (count <= 0 || fit.columns <= 0 || fit.height <= 0) return 0;

  // Half a pixel of slack: scroll positions are fractional on HiDPI screens.
  const lastFullRow = Math.floor((visibleBottom + 0.5 - fit.height) / (fit.height + gap));
  const seenRows = Math.max(0, Math.min(fit.rows, lastFullRow + 1));
  return Math.max(0, count - seenRows * fit.columns);
}

// ---------------------------------------------------------------------------
// Pinned layout (issue #124, epic §3.3)
// ---------------------------------------------------------------------------

/** The pinned tile is always 16:9 (epic D9). */
export const STAGE_ASPECT = 16 / 9;

/** Everyone else, beside or under the pinned tile, is 4:3. */
export const PINNED_TILE_ASPECT = 4 / 3;

/**
 * Width of the column beside the pinned tile: `clamp(200px, 20%, 280px)` of
 * the video area's width, so 280px on a 1440px window and 200px with the
 * chat drawer open beside it.
 */
export const PINNED_COLUMN_MIN_WIDTH = 200;
export const PINNED_COLUMN_MAX_WIDTH = 280;
export const PINNED_COLUMN_SHARE = 0.2;

/**
 * Below this video-area width there is no room for a column: the pinned tile
 * goes full width on top and everyone else in a two-column grid under it.
 */
export const PINNED_STACKED_BELOW = 640;

/**
 * In the stacked arrangement the pinned tile takes at most this share of the
 * height, so a short area still shows part of the grid under it.
 */
export const STACKED_STAGE_MAX_SHARE = 0.6;

/** A width and a height, in whole pixels. */
export interface TileSize {
  width: number;
  height: number;
}

/** A box in the video area, in pixels from its top-left corner. */
export interface LayoutBox extends TileSize {
  left: number;
  top: number;
}

const EMPTY_BOX: LayoutBox = { left: 0, top: 0, width: 0, height: 0 };

/**
 * The largest 16:9 box in a `width` × `height` area, in whole pixels.
 *
 * The limiting side is floored so the box never overflows; the other side is
 * rounded from it (1100 wide → 619 tall), then capped by the area.
 */
export function fitStage(width: number, height: number): TileSize {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };

  if (width / height > STAGE_ASPECT) {
    const stageHeight = Math.floor(height);
    return {
      width: Math.min(Math.floor(width), Math.round(stageHeight * STAGE_ASPECT)),
      height: stageHeight
    };
  }

  const stageWidth = Math.floor(width);
  return {
    width: stageWidth,
    height: Math.min(Math.floor(height), Math.round(stageWidth / STAGE_ASPECT))
  };
}

/** A 4:3 tile `width` wide. */
function pinnedTile(width: number): TileSize {
  const tileWidth = Math.max(0, Math.floor(width));
  return { width: tileWidth, height: Math.round(tileWidth / PINNED_TILE_ASPECT) };
}

/** Where the pinned layout puts everything, in video-area coordinates. */
export interface PinnedFit {
  /**
   * `column`: everyone else in one column right of the pinned tile.
   * `stacked` (narrow areas): the pinned tile on top, a two-column grid under it.
   */
  arrangement: 'column' | 'stacked';
  /** The pinned tile */
  stage: LayoutBox;
  /** The box the other tiles scroll in; empty when nobody else is in the call */
  others: LayoutBox;
  /** The other tiles' grid: one column beside the stage, two under it */
  fit: TileFit;
}

/**
 * The pinned layout for a `width` × `height` video area with `othersCount`
 * tiles beside the pinned one (epic D9).
 *
 * `inset` is the padding around the whole layout. The column's
 * `clamp(200px, 20%, 280px)` is measured against the full area, the padding
 * included, so on 1440 × 900 (an area of 1440 × 780 with 24px padding) the
 * column is 280px and the pinned tile 1100 × 619; with the chat open (840 ×
 * 780) the column is 200px and the pinned tile 580 × 326.
 *
 * - **Column** (area 640px wide or more): the pinned tile is the largest 16:9
 *   box left of the column, centred in that space. The column is 4:3 tiles
 *   the column's width, one per row, from the top; it is as tall as the area
 *   and the caller scrolls whatever does not fit.
 * - **Stacked** (narrower): the pinned tile full width on top (at most 60% of
 *   the height), and a two-column grid of 4:3 tiles under it.
 * - **Nobody else**: the pinned tile is the largest 16:9 box in the area.
 */
export function fitPinned(
  othersCount: number,
  width: number,
  height: number,
  gap: number,
  inset = 0
): PinnedFit {
  const others = Math.max(0, Math.floor(othersCount));
  const arrangement = width < PINNED_STACKED_BELOW ? 'stacked' : 'column';
  const contentWidth = width - 2 * inset;
  const contentHeight = height - 2 * inset;

  if (contentWidth <= 0 || contentHeight <= 0) {
    return { arrangement, stage: EMPTY_BOX, others: EMPTY_BOX, fit: EMPTY_FIT };
  }

  if (others === 0) {
    const stage = fitStage(contentWidth, contentHeight);
    return {
      arrangement,
      stage: {
        left: inset + Math.floor((contentWidth - stage.width) / 2),
        top: inset + Math.floor((contentHeight - stage.height) / 2),
        ...stage
      },
      others: EMPTY_BOX,
      fit: EMPTY_FIT
    };
  }

  if (arrangement === 'column') {
    const columnWidth = Math.floor(
      Math.min(PINNED_COLUMN_MAX_WIDTH, Math.max(PINNED_COLUMN_MIN_WIDTH, width * PINNED_COLUMN_SHARE))
    );
    const stageAreaWidth = Math.max(0, contentWidth - columnWidth - gap);
    const stage = fitStage(stageAreaWidth, contentHeight);
    const tile = pinnedTile(columnWidth);

    return {
      arrangement,
      stage: {
        left: inset + Math.floor((stageAreaWidth - stage.width) / 2),
        top: inset + Math.floor((contentHeight - stage.height) / 2),
        ...stage
      },
      others: {
        left: inset + contentWidth - columnWidth,
        top: inset,
        width: columnWidth,
        height: contentHeight
      },
      fit: { columns: 1, rows: others, ...tile }
    };
  }

  const stage = fitStage(contentWidth, contentHeight * STACKED_STAGE_MAX_SHARE);
  const othersTop = stage.height + gap;
  const tile = pinnedTile((contentWidth - gap) / 2);

  return {
    arrangement,
    stage: {
      left: inset + Math.floor((contentWidth - stage.width) / 2),
      top: inset,
      ...stage
    },
    others: {
      left: inset,
      top: inset + othersTop,
      width: contentWidth,
      height: Math.max(0, contentHeight - othersTop)
    },
    fit: { columns: 2, rows: Math.ceil(others / 2), ...tile }
  };
}
